import { Money } from ".";
import { amountToMinorUnits, Currency, minorUnitsToAmount } from "./currencies";

/**
 * The amount of currency that is equivalent to 1 unit of the base currency.
 */
export type ExchangeRate = number;

export interface CurrencyConverter {
  /**
   * Convert money to a different currency
   */
  convert(money: Money, currency: Currency): Money;
  /**
   * Get the exchange rate between two currencies
   */
  exchangeRate(fromCurrency: Currency, toCurrency: Currency): ExchangeRate;
  /**
   * Get all exchange rates
   */
  exchangeRates(): Map<Currency, ExchangeRate>;
}

export class CurrencyExchangeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CurrencyExchangeError";
  }
}

export function createCurrencyConverter(
  rates: Map<Currency, ExchangeRate>,
): CurrencyConverter {
  return {
    convert(money, currency) {
      if (money.currency === currency) {
        return money;
      }
      const exchangeRate = this.exchangeRate(money.currency, currency);
      return { amount: money.amount * exchangeRate, currency };
    },
    exchangeRate(fromCurrency, toCurrency) {
      if (fromCurrency === toCurrency) {
        return 1;
      }
      const fromRate = rates.get(fromCurrency);
      if (fromRate === undefined) {
        throw new CurrencyExchangeError(
          `Exchange rate for ${fromCurrency} not found`,
        );
      }
      const toRate = rates.get(toCurrency);
      if (toRate === undefined) {
        throw new CurrencyExchangeError(
          `Exchange rate for ${toCurrency} not found`,
        );
      }
      return toRate / fromRate;
    },
    exchangeRates() {
      return rates;
    },
  };
}

/** Converts a minor-unit amount between currencies, honouring each currency's
 * own exponent. Returns the input untouched when the currency is unchanged. */
export function convertMinorUnits(
  minor: number,
  from: Currency,
  to: Currency,
  converter: CurrencyConverter,
): number {
  if (from === to) {
    return minor;
  }
  const converted = converter.convert(
    { amount: minorUnitsToAmount(minor, from), currency: from },
    to,
  );
  return amountToMinorUnits(converted.amount, to);
}
